/**
 * Sentinel emitted by the divisions selector when the "all" option is picked
 * and `allValue` is a plain string. When `allValue` is an array the selector
 * emits the explicit list of ids instead, so this branch is purely defensive.
 */
export const ALL_DIVISIONS_VALUE = 'all';

/**
 * Builds the value the divisions selector should DISPLAY.
 *
 * The selector needs the explicit list of selected ids so it can mark each
 * option (including the "all"/"Todos" option) as checked. This is the opposite
 * concern from {@link normalizeDivisionIdsForSave}, which collapses a full
 * selection to an empty array (the "empty = all" persistence convention).
 *
 * Keeping these two representations separate is what fixes selecting "all"
 * showing nothing: the form holds the expanded ids for display, while saving
 * still normalizes to the compact form.
 */
export const expandSelectedDivisionIds = (
  divIds: string[],
  allowedDivisionIds: string[],
): string[] => (divIds.includes(ALL_DIVISIONS_VALUE) ? allowedDivisionIds : divIds);

/**
 * Converts persisted divisionIds into the value the selector should display.
 * An empty array in persistence means "all allowed divisions".
 */
export const expandDivisionIdsForDisplay = (
  divisionIds: string[],
  allowedDivisionIds: string[],
): string[] => {
  if (divisionIds.length === 0) {
    return allowedDivisionIds;
  }

  return expandSelectedDivisionIds(divisionIds, allowedDivisionIds);
};

export const normalizeDivisionIdsForSave = (
  divisionIds: string[],
  allowedDivisionIds: string[],
): string[] => {
  if (divisionIds.length === 0) {
    return [];
  }

  if (allowedDivisionIds.length === 0) {
    return divisionIds;
  }

  const allowedSet = new Set(allowedDivisionIds);
  const selectedAllowedIds = divisionIds.filter(id => allowedSet.has(id));

  if (selectedAllowedIds.length === allowedDivisionIds.length) {
    return [];
  }

  return selectedAllowedIds;
};

/**
 * Division ids to send to the Elastic generic-query API (and to writes that
 * use the same payload).
 *
 * Empty `divisionIds` means "every division in the organization": no security
 * filter, and documents stored as org-wide. A full selection therefore
 * collapses to `[]` **only** for org-wide roles with **more than one** allowed
 * division.
 *
 * Exceptions that must stay explicit:
 * - Division-scoped users: the backend authorizes from the requested ids, so
 *   `[]` is rejected (`permission-denied`) and would also skip the filter.
 * - A single allowed division: `normalizeDivisionIdsForSave` still collapses
 *   `[onlyDiv]` to `[]` ("all selected"), but that must not reach the API.
 *   Forms/triggers created in a one-division org are stored with that id;
 *   sending `[]` hid them from lists/pickers and blocked division-scoped
 *   creates. New writes cannot persist true org-wide `[]` in that case: if a
 *   second division is added later, those records stay scoped to the first id.
 *   Legacy org-wide docs remain readable via `includeEmptyDivisionScope`.
 */
export const resolveElasticDivisionIds = (
  selectedDivisionIds: string[],
  allowedDivisionIds: string[],
  hasOrgWideAccess: boolean,
): string[] => {
  const normalized = normalizeDivisionIdsForSave(selectedDivisionIds, allowedDivisionIds);

  // One reachable division is never "the whole organization" at the API layer.
  // `normalizeDivisionIdsForSave` collapses `[onlyDiv]` to `[]` ("all selected"),
  // and empty `divisionIds` then means both "no Elastic filter" and "org-wide
  // document". Forms and triggers created with that single division are stored
  // with the explicit id, so collapsing hid them from the picker, list, and
  // made a division-scoped create send `[]` (permission-denied).
  if (allowedDivisionIds.length === 1) {
    return allowedDivisionIds;
  }

  if (normalized.length || hasOrgWideAccess) {
    return normalized;
  }

  return expandDivisionIdsForDisplay(selectedDivisionIds, allowedDivisionIds);
};

/**
 * Division ids for reads that are not driven by a selector (fetching a single
 * document by id, or the read-back performed before an update).
 *
 * There is no user selection to honour here, so the scope is everything the
 * role can reach: `[]` for org-wide roles, and the full allowed list for a
 * division-scoped user, who would otherwise be rejected by the generic-query
 * API. Callers should pair it with `includeEmptyDivisionScope` so org-wide
 * documents stay reachable.
 */
export const resolveElasticScopeDivisionIds = (
  allowedDivisionIds: string[],
  hasOrgWideAccess: boolean,
): string[] => (hasOrgWideAccess ? [] : allowedDivisionIds);
